"use client";

import React from "react";
import Link from "next/link";
import { Menu, PanelLeftClose, PanelLeftOpen, User, UserPlus, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useUIStore } from "@/store/ui-store";
import { useChatStore } from "@/store/chat-store";
import { useAuthStore } from "@/store/auth-store";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { LanguageSelector } from "@/components/ui/language-selector";

export function Header() {
  const { sidebarOpen, setSidebarOpen } = useUIStore();
  const { chats, currentChatId } = useChatStore();
  const { user, isAuthenticated } = useAuthStore();

  const currentChat = chats.find((chat) => chat.id === currentChatId);

  const handleLogoClick = () => {
    // Close sidebar on mobile when logo is clicked
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-slate-800 bg-slate-950 px-4">
      {/* Mobile menu button - only show if authenticated */}
      {isAuthenticated && (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(true)}
          className="md:hidden"
        >
          <Menu className="h-5 w-5" />
        </Button>
      )}

      {/* Desktop sidebar toggle - only show if authenticated */}
      {isAuthenticated && (
        <div className="hidden md:block">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            title={sidebarOpen ? "Sidebar'ı kapat" : "Sidebar'ı aç"}
          >
            {sidebarOpen ? (
              <PanelLeftClose className="h-5 w-5" />
            ) : (
              <PanelLeftOpen className="h-5 w-5" />
            )}
          </Button>
        </div>
      )}

      <div className="flex items-center gap-2 min-w-0 flex-1">
        <h1 className="text-lg font-semibold truncate">
          {(!isAuthenticated || !sidebarOpen) && (
            <>
              <Link
                href="/"
                onClick={handleLogoClick}
                className="text-orange-400 hover:text-orange-300 transition-colors"
              >
                AXTROLOG
              </Link>
              {isAuthenticated && <span className="mx-2">-</span>}
            </>
          )}
          {isAuthenticated && (currentChat?.title || "Sohbet")}
        </h1>
      </div>

      <div className="flex items-center gap-2">
        {/* Mobile Controls */}
        <div className="md:hidden flex items-center gap-1">
          {/* Mobile Login/User */}
          {isAuthenticated && user ? (
            <Avatar className="h-8 w-8">
              <AvatarImage src={user.avatar} alt={user.name || user.email} />
              <AvatarFallback className="text-xs">
                {user.name ? user.name[0].toUpperCase() : user.email[0].toUpperCase()}
              </AvatarFallback>
            </Avatar>
          ) : (
            <Link href="/login">
              <Button
                variant="ghost"
                size="icon"
                title="Giriş Yap"
              >
                <User className="h-4 w-4" />
              </Button>
            </Link>
          )}

          {/* Mobile About */}
          <Link href="/about">
            <Button
              variant="ghost"
              size="icon"
              title="AXTROLOG Nedir?"
              className="text-slate-400 hover:text-slate-300"
            >
              <HelpCircle className="h-4 w-4" />
            </Button>
          </Link>

          {/* Mobile Language Selector */}
          <LanguageSelector variant="icon" />
        </div>

        {/* Desktop Controls */}
        <div className="hidden md:flex items-center gap-2">
          {/* Free Registration */}
          <Link href="/register">
            <Button
              variant="ghost"
              size="sm"
              className="text-green-400 hover:text-green-300 hover:bg-green-900/20"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Ücretsiz Kayıt Ol
            </Button>
          </Link>

          {/* User Profile or Login */}
          {isAuthenticated && user ? (
            <div className="flex items-center gap-2 p-1 rounded-lg bg-slate-800">
              <Avatar className="h-6 w-6">
                <AvatarImage src={user.avatar} alt={user.name || user.email} />
                <AvatarFallback className="text-xs">
                  {user.name ? user.name[0].toUpperCase() : user.email[0].toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium truncate max-w-20">
                {user.name || user.email}
              </span>
            </div>
          ) : (
            <Link href="/login">
              <Button
                variant="ghost"
                size="sm"
              >
                <User className="h-4 w-4 mr-2" />
                Giriş Yap
              </Button>
            </Link>
          )}

          {/* AXTROLOG Nedir */}
          <Link href="/about">
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-slate-300"
            >
              <HelpCircle className="h-4 w-4 mr-2" />
              AXTROLOG Nedir?
            </Button>
          </Link>
        </div>

        {/* Desktop Language Selector - Positioned at the very right */}
        <div className="hidden md:block">
          <LanguageSelector variant="flag" />
        </div>
      </div>
    </header>
  );
}